import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output
} from '@angular/core';
import {
  AbstractControl,
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  Validators
} from '@angular/forms';
import {
  API_PREFIX_V2,
  GlobalWebsocketService
} from '@approot/global-websocket';
import { KeyValue } from '@approot/models';
import { ExpirationSettlementDate } from '@approot/models/expiration-settlement-date';
import { SpotLadderResult } from '@approot/models/spot-ladder';
import { PricingData } from '@approot/models/strategy-payload';
import {
  AlertsService,
  LegService,
  PricingExtraService
} from '@approot/services';
import {
  DateUtilities,
  exponentialDelay,
  shortUuid,
  sleep,
  SubscriptionSink,
  Utilities
} from '@approot/utilities';
import { LegComponent } from '@approot/widgets';
import copy from 'copy-to-clipboard';
import { fromEvent } from 'rxjs';

@Component({
  selector: 'app-spot-ladder',
  templateUrl: './spot-ladder.component.html',
  styleUrls: ['./spot-ladder.component.scss']
})
export class SpotLadderComponent implements OnInit, OnDestroy {
  @Output() spotLadderOpenChanged = new EventEmitter<boolean>();
  @Input() isModalOpened = false;
  @Input() legComponents: LegComponent[];
  calculatingStatus: { status: string; message: string } = {
    status: '',
    message: ''
  };
  activeCurrencyPairs: string[] = [];
  form: FormGroup;
  step2Form: FormGroup;

  taskItem = {};

  itemCount = 0;

  stepNumber: number;

  pricingData: PricingData = {};

  // scheduleFixings: {
  //   id: number;
  // }[];

  //legItems = [{ id: 1, title: 'firsttitle' }, { id: 2, title: 'secondtitle' }];
  //legItems: LegComponent[];
  dateItems: any[];

  currencies: string[] = [];

  formControls = {
    minSpot: {
      type: 'number'
    },
    bumpMethod: {
      items: ['From Pricing Date', 'At Future Date', 'Linear'],
      key: 'bumpMethod'
    },
    outputData: {
      items: ['Spot Delta'],
      key: 'outputData'
    }
  };
  servicePayload: {
    task: {
      taskType: string;
      taskParameters: {
        cross: any;
        minSpot: any;
        maxSpot: any;
        numpts: any;
        bumpTimeMethod: any;
        reportingCurrency: any;
        includePastFlows: string | number;
        fixingCutforCashConversion: string;
        output: any[];
        dates: any[];
      };
    };
    products: any[];
  };
  graphDisplayDates: string[] = [];
  graphDisplayLegs: string[] = [];
  currentOutPutData: any;
  reportingCurrency: string;
  spotLadderResults: any;
  spotLadderResponseResult: SpotLadderResult[];

  fixingsInfo: any = {};
  private cross = '';
  private subSink = new SubscriptionSink();
  private webSocketUUID = 'spot-ladder_' + shortUuid();

  constructor(
    private legService: LegService,
    private _formBuilder: FormBuilder,
    private pricingExtraService: PricingExtraService,
    private webSocketService: GlobalWebsocketService,
    private alertsService: AlertsService
  ) {
    this.stepNumber = 1;
    this.form = this._formBuilder.group({
      cross: this._formBuilder.control(''),
      minSpot: this._formBuilder.control('', Validators.required),
      maxSpot: this._formBuilder.control('', Validators.required),
      numberOfPoints: this._formBuilder.control('', Validators.required),
      fixing1: this._formBuilder.control(''),
      fixing2: this._formBuilder.control(''),
      fixing3: this._formBuilder.control(''),
      stage1CurrencyRadio: this._formBuilder.control(''),
      bumpMethod: this._formBuilder.control('', Validators.required),
      markToMarketToggle: this._formBuilder.control(''),
      vegaToggle: this._formBuilder.control(''),
      gammaToggle: this._formBuilder.control(''),
      cashFlowsToggle: this._formBuilder.control(''),
      spotDeltaToggle: this._formBuilder.control(''),
      intermediaryFlowsToggle: this._formBuilder.control(''),
      flowsBeforeDateToggle: this._formBuilder.control('')
    });

    this.step2Form = this._formBuilder.group({
      outputData: this._formBuilder.control(''),
      stage2CurrencyRadio: this._formBuilder.control(''),
      legsFormArray: this._formBuilder.array([]),
      datesFormArray: this._formBuilder.array([])
    });

    this.form
      .get('stage1CurrencyRadio')
      .setValidators([this.reportingCurrencyValidator]);

    this.form
      .get('fixing1')
      .setValidators([Validators.required, this.fixingValidator]);
    this.form.get('fixing2').setValidators([this.fixingValidator]);
    this.form.get('fixing3').setValidators([this.fixingValidator]);
  }

  fixingValidator(control: AbstractControl): KeyValue | null {
    if (control.value === '') {
      return null;
    }

    const crossValue = control.parent.get('cross').value;
    if (crossValue) {
      if (
        DateUtilities.isValidLag(control.value) ||
        DateUtilities.isCorrectDateFormat(control.value)
      ) {
        return null;
      }
    } else if (
      DateUtilities.isDateOfSlashFormat(control.value) ||
      DateUtilities.isPrettyDate(control.value)
    ) {
      return null;
    }
    return { invalidEntry: { value: control.value } };
  }

  reportingCurrencyValidator(control: AbstractControl): KeyValue | null {
    const crossValue = control.parent.get('cross').value;
    if (crossValue) {
      return control.value ? null : { invalidEntry: { value: control.value } };
    }
    return null;
  }

  public ngOnDestroy(): void {
    this.subSink.unsubscribe();
  }

  get legsFormArray() {
    return this.step2Form.get('legsFormArray') as FormArray;
  }

  get legsFormArrayControls() {
    return this.legsFormArray.controls as FormControl[];
  }

  get datesFormArray() {
    return this.step2Form.get('datesFormArray') as FormArray;
  }

  get datesFormArrayControls() {
    return this.datesFormArray.controls as FormControl[];
  }

  reportingCurrencyChanged(index: number) {
    this.reportingCurrency = index === 0 ? 'baseCurrency' : 'quoteCurrency';
  }

  ngOnInit() {
    // we need at least 1 date to be provided
    // this.fixingsFormArray.push(
    //   this._formBuilder.control('', Validators.required)
    // );

    this.webSocketListener();

    this.step2Form.get('outputData').valueChanges.subscribe((val: string) => {
      switch (val.toLowerCase()) {
        case 'vega':
          this.currentOutPutData = 'vega';
          break;
        case 'spotdelta':
          this.currentOutPutData = 'spotDelta';
          break;
        case 'gamma':
          this.currentOutPutData = 'gamma';
          break;
        case 'price':
          this.currentOutPutData = 'priceForUI';
          break;
        default:
          break;
      }
    });

    //this.legItems = this.legComponents;

    this.legComponents.forEach(() => {
      this.legsFormArray.push(this._formBuilder.control(''));
    });

    // this.scheduleFixings = [];

    // really, we should define this as LegComponent type, not any
    // (but this makes creating test object of LegComponent difficult)
    this.legComponents.map((x: any) => {
      // there can't be duplicates of each currencyPair, so check if it already exists in the array before adding it.
      if (
        x.form.controls['cross'] &&
        x.form.controls['cross'].value &&
        this.activeCurrencyPairs.indexOf(x.form.controls['cross'].value) === -1
      ) {
        this.activeCurrencyPairs.push(x.form.controls['cross'].value);
      }

      if (
        x.form.controls['secondCross'] &&
        x.form.controls['secondCross'].value &&
        this.activeCurrencyPairs.indexOf(
          x.form.controls['secondCross'].value
        ) === -1
      ) {
        this.activeCurrencyPairs.push(x.form.controls['secondCross'].value);
      }
    });

    this.formControls['cross'] = {
      type: 'enum',
      required: true,
      attributes: {
        options: this.activeCurrencyPairs,
        key: 'cross'
      }
    };

    this.form.get('cross').valueChanges.subscribe(newSelection => {
      this.cross = newSelection;
      this.currencies = [];
      if (newSelection) {
        this.currencies.push(newSelection.substring(0, 3));
        this.currencies.push(newSelection.substr(newSelection.length - 3));
      }
      this.form.get('stage1CurrencyRadio').updateValueAndValidity(); // field is not required if no cross
    });

    this.form.get('cross').setValue(this.activeCurrencyPairs[0]);

    ['fixing1', 'fixing2', 'fixing3'].forEach(fixing => {
      this.fixingsInfo[fixing] = { info: '' };
      const control = this.form.get(fixing);
      control.valueChanges.subscribe(val => {
        this.fixingsInfo[fixing].info = '';
        if (
          this.cross &&
          control.valid &&
          (DateUtilities.isCorrectDateFormat(val) ||
            DateUtilities.isValidLag(val))
        ) {
          control.disable();
          const payload = DateUtilities.dateOrLagToPayload(
            val,
            this.cross,
            this.pricingExtraService.getMarketDataTimeStamp
          );
          control.markAsPending();
          this.legService.calculateFutureDate(payload).then(
            resp => {
              resp.subscribe(
                (dates: ExpirationSettlementDate) => {
                  control.enable({ emitEvent: false });
                  if (dates.expirationDate && dates.settlementDate) {
                    control.patchValue(
                      DateUtilities.dateToPrettyDate(dates.expirationDate),
                      { emitEvent: false }
                    );

                    if (DateUtilities.isValidLag(payload.dateOrLag)) {
                      this.fixingsInfo[fixing].info =
                        '(' + payload.dateOrLag + ')';
                    }
                  } else {
                    console.error('Unable to retrieve date.');
                  }
                },
                () => {
                  control.enable({ emitEvent: false });
                }
              );
            },
            () => {
              control.enable({ emitEvent: false });
            }
          );
        } else {
          DateUtilities.onDateChanged(control);
        }
      });
    });

    this.subSink.sink = fromEvent(document, 'keydown').subscribe(event => {
      // we want to listen to the f9 button press
      this.keypress(event);
    });
  }

  private webSocketListener(): void {
    const resubscribe = (iteration: number) => {
      this.calculatingStatus.status = '';
      if (iteration > 5) {
        this.calculatingStatus = {
          status: 'webSocketDown',
          message: ''
        };

        this.alertsService.addAlert({
          message:
            'There was a problem setting up the WebSocket for the spot ladder',
          type: 'error'
        });
        return;
      }
      this.subSink.sink = this.webSocketService
        .getListener({
          action: `${API_PREFIX_V2}pricing`,
          uuid: this.webSocketUUID,
          take: Infinity
        })
        .subscribe({
          next: resp => {
            if (resp.error) {
              this.alertsService.addAlert({
                message: resp.error?.message,
                type: 'error'
              });
              this.calculatingStatus = {
                status: '',
                message: ''
              };
            } else {
              this.handleSpotLadderPayload(resp.payload as SpotLadderResult[]);
            }
          },
          complete: async () => {
            await sleep(exponentialDelay(iteration));
            resubscribe(iteration + 1);
          },
          error: async _ => {
            await sleep(exponentialDelay(iteration));
            resubscribe(iteration + 1);
          }
        });
    };
    resubscribe(0);
  }

  private handleSpotLadderPayload(payload: SpotLadderResult[]): void {
    this.spotLadderResponseResult.push(payload[0]);
    if (this.spotLadderResponseResult.length === this.itemCount) {
      this.calculatingStatus = {
        status: '',
        message: ''
      };
      this.spotLadderResults = this.spotLadderResponseResult;
      this.stepNumber = 2;
      this.setUpDefaults();
    }
  }

  keypress(event) {
    if (
      event.keyCode === 120 &&
      this.calculatingStatus.status !== 'calculating' &&
      this.calculatingStatus.status !== 'webSocketDown'
    ) {
      // if the key is f9 and the forms are valid, calculate the spot
      if (this.form.valid) {
        // if the form is valid then calculate the spot
        this.onSubmitStep1();
      }
    }
  }

  dateToggleChange(date, event) {
    // if the date is toggled 'on' add it to the array of dates to graph
    // otherwise remove it from that array
    event.target.checked
      ? this.graphDisplayDates.push(date)
      : this.graphDisplayDates.splice(this.graphDisplayDates.indexOf(date), 1);
    // need to reset this array to trigger change detection in the spot-ladder-graph component
    this.graphDisplayDates = JSON.parse(JSON.stringify(this.graphDisplayDates));
  }

  legToggleChange(leg, event) {
    event.target.checked
      ? this.graphDisplayLegs.push(leg)
      : this.graphDisplayLegs.splice(this.graphDisplayLegs.indexOf(leg), 1);

    this.graphDisplayLegs = JSON.parse(JSON.stringify(this.graphDisplayLegs));
  }

  modalChanged(event) {
    this.spotLadderOpenChanged.emit(event);
  }

  cancel() {
    this.modalChanged(false);
  }

  backToSetup() {
    this.stepNumber = 1;
  }

  // where we have 1+ legs that need treasury curve data

  onSubmitStep1() {
    this.calculatingStatus = { status: 'calculating', message: '' };

    this.datesFormArray.clear();
    this.itemCount = 0;
    this.spotLadderResponseResult = [];

    this.graphDisplayLegs = []; // wipe the graph data as defaults will be set later
    this.graphDisplayDates = [];
    this.dateItems = this.dateSetup();
    this.dateItems.forEach(() => {
      this.datesFormArray.push(this._formBuilder.control(''));
    });
    if (!this.dateItems.length)
      throw new Error('unable to find date values for spot ladder');

    // taskItem will store all the calls to priceLeg (to be passed into forkJoin)

    this.dateItems.forEach(date => {
      // take all the parameters from the first form
      let task = {
        taskType: 'spotLadder',
        taskParameters: {
          cross: this.form.get('cross').value,
          minSpot: this.form.get('minSpot').value,
          maxSpot: this.form.get('maxSpot').value,
          numpts: this.form.get('numberOfPoints').value,
          bumpTimeMethod: this.form
            .get('bumpMethod')
            .value.replace(/\s+/g, '')
            .toLowerCase(),
          reportingCurrency: this.form.get('stage1CurrencyRadio').value,
          includePastFlows:
            this.form.get('intermediaryFlowsToggle').value === true ? '1' : 0,
          includeFlowsBeforeValueDate:
            this.form.get('flowsBeforeDateToggle').value === true ? '1' : 0,
          fixingCutforCashConversion: 'NY_1000',
          output: this.outputSetup(),
          dates: [date]
        }
      };

      if (!task.taskParameters.cross) {
        delete task.taskParameters.cross;
        delete task.taskParameters.reportingCurrency;
      }

      //join all the market data from all the legs
      this.pricingData = {
        fixings: [],
        treasuryRates: [],
        marketData: []
      };
      let filteredRequiredFixings = [];

      // get fixings
      this.legComponents.forEach((legComponent: LegComponent) => {
        if (
          legComponent.legHelperService.requiredFixings &&
          (legComponent.form.get('fixingSource') ||
            legComponent.form.get('cutOff'))
        ) {
          // like in the leg component pricing at times the fixingSource or cutOff may not exist on a particular leg
          let fixings = legComponent.legPricingService.getFixings();
          filteredRequiredFixings = fixings.concat(fixings);
        }
      });

      this.pricingData.fixings = filteredRequiredFixings;

      // get legs that need treasury curves
      let payload = this.legService.createServicePayload(
        this.pricingExtraService.getMarketDataTimeStamp,
        this.pricingExtraService.marketDataSpreadingRule,
        this.legComponents.map(y => {
          return { product: y.form.value, attributes: y.leg.attributes };
        }),
        task,
        this.pricingData
      );
      this.itemCount++;
      this.webSocketService.sendMessage({
        action: `${API_PREFIX_V2}pricing`,
        payload,
        uuid: this.webSocketUUID
      });
    });
  }

  formatGraphDataToExcelFormat() {
    // copying the data to excel format is a tiny bit more complicated.
    // this is due to that the dates, which contains legs within them, are stored within different responses
    // all dates of legs should be stored under a single 'column' this results in more decisions being needing to be made
    const datesToShow = [];
    const legsToShow = this.graphDisplayLegs;

    let outputDataSelected = '';
    let baseOrQuoteSelected =
      this.currencies.indexOf(
        this.step2Form.get('stage2CurrencyRadio').value
      ) === 0
        ? 'baseCurrency'
        : 'quoteCurrency';

    if (this.step2Form.get('outputData').value) {
      switch (this.step2Form.get('outputData').value.toLowerCase()) {
        // we need to convert the output data form name to the name that the server returns
        case 'vega':
          outputDataSelected = 'vega';
          break;
        case 'spotdelta':
          outputDataSelected = 'spotDelta';
          break;
        case 'gamma':
          outputDataSelected = 'gamma';
          break;
        case 'price':
          outputDataSelected = 'priceForUI';
          break;
      }
    }

    let tempLegHeaders = [''];
    let tempHeaders = ['spot'];

    // these hold the dates/ table headers in a simple row

    // the data of the table has an unknown number of rows, store them in this array
    let tempData = [];
    this.spotLadderResults
      .filter(date => {
        // we only want the dates we are interested in
        return this.graphDisplayDates.indexOf(date.date) !== -1;
      })
      .map(date => {
        datesToShow.push(date.date); // the order of the dates should be the same as the order of the response
        return date[outputDataSelected][baseOrQuoteSelected].filter(
          leg => legsToShow.indexOf(leg.name) !== -1
        ); // only keep the legs that we are interested in
      })
      .forEach((dateLegs, dateIndex) => {
        dateLegs.forEach((leg, legIndex) => {
          if (dateIndex === 0) {
            // we want to add the leg headers only once
            // as the headers e.g. leg1 leg2 so do this on the first date response
            if (datesToShow.length > 1) {
              // if there are multiple dates we will need to add padding e.g. leg1         leg2
              tempLegHeaders = [
                ...tempLegHeaders,
                leg.name,
                ...Array(datesToShow.length - 1).fill('') // adds padding based on the number of dates needed
              ];
            } else {
              // if there is only one date no need to add padding
              tempLegHeaders.push(leg.name);
            }
          }

          leg.data.forEach((legData, legDataIndex) => {
            // we go through the leg data
            if (dateIndex === 0 && legIndex === 0) {
              // we want to add the spot to the beginning of the row only when we are adding the first data to the row
              // if we are on the first leg that should be added, and the first date add the spot for the row
              tempData.push([this.spotLadderResults[0].spots[legDataIndex]]);
            }

            tempData[legDataIndex].splice(
              1 + legIndex * (dateIndex + 1) + dateIndex,
              0,
              legData
            );

            // this is when the data is inserted
            // the position starts at 1 which is the index that is first available, after the spot column title
            // plus the index of the leg times the amount of dates that have been added.
            // this gets the index to a position where the first values of the leg starts,
            // then we add the current date index to get into the position that we will add the new data at.

            if (legDataIndex === 0) {
              // on the first run, add the date header.
              tempHeaders.splice(
                1 + legIndex * (dateIndex + 1) + dateIndex,
                0,
                DateUtilities.dateToPrettyDate(datesToShow[dateIndex])
              );
            }
          });
        });
      });

    return Utilities.arrayToExcelFormat(
      tempData.length ? [tempLegHeaders, tempHeaders, ...tempData] : []
    );
  }

  onCopyToClipboardClicked() {
    copy(this.formatGraphDataToExcelFormat(), { format: 'text/plain' });
    // copy the text. keep in text/plain, so that tabs and new lines stay
  }

  setUpDefaults() {
    // this sets up the default values for the stage 2 options
    if (this.formControls.outputData.items.length) {
      // select the first output data type
      this.step2Form
        .get('outputData')
        .setValue(this.formControls.outputData.items[0]);
    }

    if (this.currencies.length) {
      // second currency should always be the selected one
      this.step2Form.get('stage2CurrencyRadio').setValue(this.currencies[1]);
      this.reportingCurrencyChanged(1);
    }

    if (this.legsFormArray.controls.length) {
      this.legsFormArray.controls.map((control, index) => {
        this.graphDisplayLegs.push('leg' + (index + 1));
        control.setValue(true);
      });
    }

    if (this.datesFormArray.controls.length) {
      this.datesFormArray.controls.map((control, index) => {
        this.graphDisplayDates.push(this.dateItems[index]);
        control.setValue(true);
      });
    }
    this.graphDisplayDates = JSON.parse(JSON.stringify(this.graphDisplayDates));
  }

  outputSetup() {
    let outputDataItems = [];

    if (this.form.get('markToMarketToggle').value === true) {
      outputDataItems.push('Price');
    }

    if (this.form.get('spotDeltaToggle').value === true) {
      outputDataItems.push('SpotDelta');
    }

    if (this.form.get('vegaToggle').value === true) {
      outputDataItems.push('Vega');
    }

    if (this.form.get('gammaToggle').value === true) {
      outputDataItems.push('Gamma');
    }

    if (this.form.get('cashFlowsToggle').value === true) {
      outputDataItems.push('CashFlows');
    }

    this.formControls.outputData.items = outputDataItems;
    return outputDataItems;
  }

  dateSetup() {
    // let dates = (this.form.get('fixingsFormArray') as FormArray).controls;
    // let spotLadderDates = [];
    // for (let i = 0; i < dates.length; i++) {
    //   if (dates[i].value) {
    //     spotLadderDates.push(DateUtilities.prettyDateToDate(dates[i].value));
    //   }
    // }
    // return spotLadderDates;
    let spotLadderDates = [];
    for (let i = 1; i <= 3; i++) {
      if (this.form.get('fixing' + i).value) {
        spotLadderDates.push(
          DateUtilities.prettyDateToDate(this.form.get('fixing' + i).value)
        );
      }
    }
    return spotLadderDates;
  }
}
